import React, { createContext } from 'react'

import { serverSideQuery, useQuery } from '@/api'
import { DEFAULT_CONTEXT_VALUES, LISTINGS_ENDPOINT } from '@/constants'

import { deliveryOptionsSelector, listingSelector, i18nListingDataSelector } from './selectors'
import type {
    ListingContextType,
    ListingParamsType,
    ListingProviderProps,
    ListingResponse,
    ListingSelectorsType,
    ListingStateType,
} from './types'

const ListingContext = createContext<ListingContextType>({
    ...DEFAULT_CONTEXT_VALUES,
    dispatch: { refetch: () => undefined },
})
const { Provider } = ListingContext

const getListingEndpoint = (listingId: string): string => `${LISTINGS_ENDPOINT}/${listingId}`

export const ListingProvider: React.FC<ListingProviderProps> = ({ children, initialParams, initialProps }) => {
    const { listingId, ...queryParams } = initialParams
    const listingEndpoint: string = getListingEndpoint(listingId)

    const { fetching, data, error, refetch } = useQuery<ListingResponse>({
        endpoint: listingEndpoint,
        params: queryParams,
        initialFetchDisabled: !!initialProps?.data,
        initialData: initialProps?.data,
        enabled: !!listingId && !!queryParams.productionId,
    })

    const state: ListingStateType = { fetching, data, error }

    const selectors: ListingSelectorsType = {
        listing: listingSelector(state),
        deliveryOptions: deliveryOptionsSelector(state),
        i18nListingData: i18nListingDataSelector(state),
    }

    return <Provider value={{ state, selectors, dispatch: { refetch } }}>{children}</Provider>
}

export const serverSideRead = async (params: ListingParamsType, maxAgeMs?: number): Promise<ListingResponse> => {
    const { listingId, ...queryParams } = params
    const listingEndpoint: string = getListingEndpoint(listingId)
    const queryInstance = serverSideQuery<ListingResponse>(listingEndpoint)
    const listingResponse: ListingResponse = await queryInstance(queryParams, maxAgeMs)
    return listingResponse
}

export default ListingContext
